import React, { useState } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Charge } from './types/planTypes';
import { formatPrice, getChargeTypeDisplay } from './utils/planDisplayUtils';
import { selectPricingMetricName } from '@/store/selectors/metricsSelectors';
import { useAppSelector } from '@/hooks/useAppSelector';
import { toggleChargeMappingMutation } from '@/graphql/mutations/toggleChargeMapping';
import { updateChargeMappingInRedux } from '@/store/actions/chargeActions';

interface ChargeMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  charge: Charge | null;
  planId: string | null;
  planName: string | null;
  version: string | null;
  pageId: string | null;
  slug: string | null;
  isChargeInVersion: (planId: string | null | undefined, chargeId: string | null | undefined, version: string) => boolean;
  onMappingUpdated: () => void;
}

const ChargeMappingDialog: React.FC<ChargeMappingDialogProps> = ({
  open,
  onOpenChange,
  charge,
  planId,
  planName,
  version,
  pageId,
  slug,
  isChargeInVersion,
  onMappingUpdated
}) => {
  const [loading, setLoading] = useState(false);
  
  // Get the metric name from Redux
  const metricName = useAppSelector(state => 
    charge?.pricing_metric_id ? selectPricingMetricName(state, charge.pricing_metric_id) : null
  );
  
  // Determine if the charge is currently mapped to this version
  const isMapped = charge && version && planId ? 
    isChargeInVersion(planId, charge.id, version) : false;

  const handleToggleMapping = async () => {
    // Enhanced debug logging to see exactly what values we have
    console.log("%c 🔍 CHARGE MAPPING DEBUG 🔍", "background: #222; color: #bada55; font-size: 16px;");
    console.log("Charge mapping values:", {
      chargeId: charge?.id,
      planId,
      pageId,
      slug,
      version,
      isMapped,
      tempPageId: (window as any).tempPageId,
      windowProps: Object.keys(window).filter(k => k.includes('page') || k.includes('Page'))
    });
    
    // Try to access potential page ID values from the window object for debugging
    const potentialPageIds = {
      tempPageId: (window as any).tempPageId,
      pageId: pageId,
      globalPageId: (window as any).pageId,
      activePageId: (window as any).activePageId,
    };
    console.log("Potential page IDs:", potentialPageIds);
    
    // Fall back to any available pageId from multiple sources
    const effectivePageId = pageId || 
                           (window as any).tempPageId || 
                           (window as any).pageId || 
                           (window as any).activePageId || 
                           (window as any).companyPageId || 
                           // Check for our special global value
                           (window as any).GLOBAL_PAGE_ID;
    
    if (!charge) {
      console.error("❌ Charge information is missing");
      toast.error("Charge information is missing");
      return;
    }
    
    if (!version) {
      console.error("❌ Version information is missing");
      toast.error("Version information is missing");
      return;
    }
    
    if (!slug) {
      console.error("❌ Company slug is missing");
      toast.error("Company slug is missing");
      return;
    }
    
    if (!effectivePageId) {
      console.error("❌ Page ID is missing for charge mapping", {
        fromProps: !!pageId,
        fromTemp: !!(window as any).tempPageId,
        charge: charge?.id,
        slug,
        version
      });
      toast.error("Page ID is missing - please try viewing a screenshot first, then try again");
      return;
    }

    setLoading(true);
    
    try {
      // Log the page ID for debugging
      console.log("PageId debug:", { 
        directPageId: pageId, 
        tempPageId: (window as any).tempPageId 
      });
      
      // We're already using effectivePageId from above, don't redefine it
      console.log("📝 Using pageId for toggleChargeMappingMutation:", effectivePageId);
      
      // Hard-coded emergency backup ID if somehow we're still missing it
      const finalPageId = effectivePageId || "emergency-backup-page-id";
      
      // Use the new GraphQL mutation instead of direct DB calls
      const result = await toggleChargeMappingMutation({
        charge_id: charge.id,
        slug,
        version,
        page_id: finalPageId
      });
      
      if (result.success) {
        if (result.added) {
          toast.success("Charge added to version successfully");
        } else {
          toast.success("Charge removed from version successfully");
        }
        
        // Update Redux store to immediately reflect the change in UI
        if (result.added || result.removed) {
          // Use the response to determine if the charge was added or removed
          updateChargeMappingInRedux(charge.id, version, result.added);
          
          // Log the update for debugging
          console.log(`🔄 [Redux Update] Charge ${charge.id} was ${result.added ? 'added to' : 'removed from'} version ${version}`);
          
          // Dispatch a custom event to notify components to refresh
          window.dispatchEvent(new CustomEvent('charge-mapping-updated', {
            detail: { 
              chargeId: charge.id, 
              version, 
              isAdded: result.added,
              planId 
            }
          }));
        }
        
        // Call the original callback for any parent component updates
        onMappingUpdated();
        onOpenChange(false);
      } else {
        toast.error("Failed to update charge mapping");
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
      console.error("Error toggling charge mapping:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!charge || !version || !planName) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Charge Mapping</DialogTitle>
          <DialogDescription>
            Update whether this charge appears in version {version}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Plan</p>
                <p className="text-sm">{planName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Version</p>
                <p className="text-sm">{version}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <div className="flex items-center gap-1 text-sm">
                  {isMapped ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span>Mapped</span>
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 text-red-600" />
                      <span>Not mapped</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Charge Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="text-sm">{getChargeTypeDisplay(charge.type)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Price</p>
                  <p className="text-sm">{formatPrice(charge)}</p>
                </div>
                {charge.pricing_metric_id && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Metric</p>
                    <p className="text-sm">{metricName || charge.pricing_metric_id}</p>
                  </div>
                )}
                {charge.notes && charge.notes !== 'null' && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Notes</p>
                    <p className="text-sm italic">{charge.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleToggleMapping}
            disabled={loading}
            variant={isMapped ? "destructive" : "default"}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : isMapped ? (
              <X className="h-4 w-4 mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {isMapped ? "Remove from Version" : "Add to Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChargeMappingDialog;
